import React, { useEffect, useMemo, useState } from "react";
import { Search, MapPin, Building2, MessageCircle, Handshake } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { api } from "../../lib/api";
import { WHATSAPP_NUMBER } from "../../lib/data";

export const DistributorDirectory = () => {
  const { t } = useLang();
  const d = t.distributor;
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState("all");

  useEffect(() => {
    const load = () =>
      api.get("/distributors/appointed").then(({ data }) => setItems(Array.isArray(data) ? data : [])).catch(() => setItems([]));
    load();
    window.addEventListener("distributor-applied", load);
    return () => window.removeEventListener("distributor-applied", load);
  }, []);

  const countries = useMemo(() => [...new Set(items.map((i) => i.country))].sort(), [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((i) => {
      const matchesCountry = country === "all" || i.country === country;
      const matchesQuery = !q || `${i.company} ${i.city} ${i.country}`.toLowerCase().includes(q);
      return matchesCountry && matchesQuery;
    });
  }, [items, query, country]);

  const enquire = (i) => {
    const msg = d.directoryEnquireMsg.replace("{company}", i.company).replace("{city}", i.city || i.country);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  const goApply = () => document.getElementById("distributor-apply")?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <div className="mt-16 md:mt-24" data-testid="distributor-directory">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="max-w-2xl">
          <h3 className="font-display font-extrabold tracking-tight text-2xl md:text-3xl text-white">{d.directoryTitle}</h3>
          <p className="mt-2 text-white/60">{d.directorySub}</p>
        </div>
        {items.length > 0 && (
          <span className="text-sm text-white/45 whitespace-nowrap" data-testid="directory-count">
            {filtered.length} {filtered.length === 1 ? d.directoryResults : d.directoryResultsPlural}
          </span>
        )}
      </div>

      {items.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-white/10 bg-black/30 p-10 text-center" data-testid="directory-empty">
          <p className="text-white/55 max-w-md mx-auto">{d.directoryEmpty}</p>
          <button
            type="button"
            onClick={goApply}
            data-testid="directory-become-cta"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-lime2 px-5 py-2.5 text-sm font-semibold text-black hover:brightness-110 transition"
          >
            <Handshake size={15} /> {d.directoryBecome}
          </button>
        </div>
      ) : (
        <>
          {/* Search + country filter */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/35" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={d.directorySearch}
                data-testid="directory-search"
                className="w-full h-12 rounded-full bg-charcoal border border-white/15 pl-11 pr-4 text-sm text-white placeholder:text-white/35 focus:border-lime2 focus:outline-none transition-colors"
              />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="directory-country-filter">
            <button
              type="button"
              onClick={() => setCountry("all")}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${country === "all" ? "border-lime2 bg-lime2/10 text-lime2" : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"}`}
            >
              {d.directoryAll}
            </button>
            {countries.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCountry(c)}
                data-testid={`directory-filter-${c}`}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${country === c ? "border-lime2 bg-lime2/10 text-lime2" : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"}`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Results grid */}
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-results">
            {filtered.map((i, idx) => (
              <div
                key={`${i.company}-${i.city}-${idx}`}
                data-testid="directory-card"
                className="rounded-2xl border border-white/10 bg-[#181818] p-5 flex flex-col hover:border-lime2/40 transition-colors"
              >
                <p className="font-display font-bold text-lg text-white inline-flex items-center gap-2">
                  <Building2 size={16} className="text-emerald2 shrink-0" /> {i.company}
                </p>
                <p className="mt-1.5 text-sm text-lime2 inline-flex items-center gap-1.5">
                  <MapPin size={13} /> {[i.city, i.country].filter(Boolean).join(", ")}
                </p>
                <button
                  type="button"
                  onClick={() => enquire(i)}
                  data-testid="directory-enquire"
                  className="mt-5 inline-flex items-center justify-center gap-2 rounded-full border border-lime2/40 text-lime2 hover:bg-lime2/10 font-semibold h-10 text-sm transition-colors"
                >
                  <MessageCircle size={15} /> {d.directoryEnquire}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
